"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.trackClsAsStandaloneSpan = trackClsAsStandaloneSpan;

var _core = require("@sentry/core");

var _debugBuild = require("../debug-build.js");

var _instrument = require("./instrument.js");

var _utils = require("./utils.js");

var _onHidden = require("./web-vitals/lib/onHidden.js");

/**
 * Starts tracking the Cumulative Layout Shift on the current page and collects the value once
 *
 * - the page visibility is hidden
 * - a navigation span is started (to stop CLS measurement for SPA soft navigations)
 *
 * Once either of these events triggers, the CLS value is sent as a standalone span and we stop
 * measuring CLS.
 */
function trackClsAsStandaloneSpan() {
  let standaloneCLsValue = 0;
  let standaloneClsEntry;
  let pageloadSpanId;

  if (!supportsLayoutShift()) {
    return;
  }

  let sentSpan = false;

  function _collectClsOnce() {
    if (sentSpan) {
      return;
    }

    sentSpan = true;

    if (pageloadSpanId) {
      sendStandaloneClsSpan(standaloneCLsValue, standaloneClsEntry, pageloadSpanId);
    }

    cleanupClsHandler();
  }

  const cleanupClsHandler = (0, _instrument.addClsInstrumentationHandler)(({
    metric
  }) => {
    const entry = metric.entries[metric.entries.length - 1];

    if (!entry) {
      return;
    }

    standaloneCLsValue = metric.value;
    standaloneClsEntry = entry;
  }, true); // use pagehide event from web-vitals

  (0, _onHidden.onHidden)(() => {
    _collectClsOnce();
  }); // Since the call chain of this function is synchronous and evaluates before the SDK client is created,
  // we need to wait with subscribing to a client hook until the client is created. Therefore, we defer
  // to the next tick after the SDK setup.

  setTimeout(() => {
    const client = (0, _core.getClient)();

    if (!client) {
      return;
    }

    const unsubscribeStartNavigation = client.on('startNavigationSpan', () => {
      _collectClsOnce();

      unsubscribeStartNavigation && unsubscribeStartNavigation();
    });
    const activeSpan = (0, _core.getActiveSpan)();
    const rootSpan = activeSpan && (0, _core.getRootSpan)(activeSpan);
    const spanJSON = rootSpan && (0, _core.spanToJSON)(rootSpan);

    if (spanJSON && spanJSON.op === 'pageload') {
      pageloadSpanId = rootSpan.spanContext().spanId;
    }
  }, 0);
}

function sendStandaloneClsSpan(clsValue, entry, pageloadSpanId) {
  _debugBuild.DEBUG_BUILD && _core.logger.log(`Sending CLS span (${clsValue})`);
  const startTime = (0, _utils.msToSec)((_core.browserPerformanceTimeOrigin || 0) + (entry && entry.startTime || 0));
  const routeName = (0, _core.getCurrentScope)().getScopeData().transactionName;
  const name = entry ? (0, _core.htmlTreeAsString)(entry.sources[0] && entry.sources[0].node) : 'Layout shift';
  const attributes = (0, _core.dropUndefinedKeys)({
    [_core.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.http.browser.cls',
    [_core.SEMANTIC_ATTRIBUTE_SENTRY_OP]: 'ui.webvital.cls',
    [_core.SEMANTIC_ATTRIBUTE_EXCLUSIVE_TIME]: entry && entry.duration || 0,
    // attach the pageload span id to the CLS span so that we can link them in the UI
    'sentry.pageload.span_id': pageloadSpanId
  });
  const span = (0, _utils.startStandaloneWebVitalSpan)({
    name,
    transaction: routeName,
    attributes,
    startTime
  });

  if (span) {
    span.addEvent('cls', {
      [_core.SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_UNIT]: '',
      [_core.SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_VALUE]: clsValue
    }); // LayoutShift performance entries always have a duration of 0, so we don't need to add `entry.duration` here
    // see: https://developer.mozilla.org/en-US/docs/Web/API/PerformanceEntry/duration

    span.end(startTime);
  }
}

function supportsLayoutShift() {
  try {
    return PerformanceObserver.supportedEntryTypes.includes('layout-shift');
  } catch (e) {
    return false;
  }
}